// src/services/pdf/data/computeGPActions.ts
// Derives recommended GP actions from summary data + PW questionnaire responses.
// Maps to NICE NG194 postnatal guidelines. Max 6 items.

import type { GPSummaryData } from "../../../types/gpSummary";
import type { GPAction, PWQuestionnaireResponse } from "../types";

/**
 * Compute up to 6 GP action items from aggregated data.
 * Each action is colour-coded (red/amber/green) and mapped to a NICE guideline.
 */
export function computeGPActions(
  data: GPSummaryData,
  pwResponses: PWQuestionnaireResponse[]
): GPAction[] {
  const actions: GPAction[] = [];

  // ── 🔴 Assess declining mood -consider IAPT referral ─────────────────────
  // Trigger: Mood W6 avg < 2.5 or declining trend
  if (data.mood.length >= 2) {
    const lastMood = data.mood[data.mood.length - 1];
    const earlyMood = data.mood.slice(0, 2);
    const earlyAvg = earlyMood.reduce((s, m) => s + m.score, 0) / earlyMood.length;

    if (lastMood.score < 2.5 || (earlyAvg - lastMood.score >= 0.5)) {
      actions.push({
        urgency: "red",
        urgencyLabel: "Act today",
        action: "Assess declining mood trend - consider IAPT referral",
        trigger: `W6 mood: ${lastMood.score}/5${lastMood.score < 2.5 ? " (below threshold)" : ""}`,
        niceRef: "NG194 1.4",
      });
    }
  }

  // ── 🔴 Refer to pelvic floor physio ───────────────────────────────────────
  // Trigger: Bladder symptom logged in Week 5 or 6
  const bladderSymptom = data.symptoms.find((s) => s.name === "Bladder Control");
  if (bladderSymptom && bladderSymptom.severity !== "none" && bladderSymptom.status !== "resolved") {
    actions.push({
      urgency: "red",
      urgencyLabel: "Act today",
      action: "Refer to pelvic floor physio for ongoing bladder symptoms",
      trigger: `Bladder control: ${bladderSymptom.status}, severity ${bladderSymptom.severity}`,
      niceRef: "NG210",
    });
  }

  // ── 🔴 Pelvic heaviness -possible prolapse ──────────────────────────────
  const pelvicSymptom = data.symptoms.find((s) => s.name === "Pelvic Heaviness");
  if (pelvicSymptom && pelvicSymptom.severity === "high") {
    actions.push({
      urgency: "red",
      urgencyLabel: "Act today",
      action: "Assess pelvic heaviness - consider prolapse referral",
      trigger: `Severity: ${pelvicSymptom.severity}, status: ${pelvicSymptom.status}`,
      niceRef: "NG210",
    });
  }

  // ── 🟠 Discuss and prescribe contraception ────────────────────────────────
  // Trigger: FTU.A.6 does not include contraception option
  // We check if PW questionnaire week 1 includes contraception-related answers
  const hasContraceptionDiscussion = pwResponses.some((pw) =>
    Object.values(pw.answers).some((a) =>
      a.toLowerCase().includes("contraception") || a.toLowerCase().includes("contraceptive")
    )
  );
  if (!hasContraceptionDiscussion) {
    actions.push({
      urgency: "amber",
      urgencyLabel: "Before next appt",
      action: "Discuss and prescribe contraception",
      trigger: "Contraception not yet discussed in programme",
      niceRef: "NG194 1.7",
    });
  }

  // ── 🟠 Confirm vitamin D supplementation ─────────────────────────────────
  // Trigger: Breastfeeding confirmed in daily logs
  const breastfeedingPain = data.symptoms.find((s) => s.name === "Breastfeeding Pain");
  if (breastfeedingPain) {
    actions.push({
      urgency: "amber",
      urgencyLabel: "Before next appt",
      action: "Confirm vitamin D supplementation",
      trigger: "Breastfeeding indicated by symptom tracking",
      niceRef: "NG194 1.9",
    });
  }

  // ── 🟠 Wound check if C-section reported ─────────────────────────────────
  const csectionWound = data.symptoms.find((s) => s.name === "C-Section Wound");
  if (csectionWound && csectionWound.severity !== "none" && csectionWound.status !== "resolved") {
    actions.push({
      urgency: "amber",
      urgencyLabel: "Before next appt",
      action: "Review C-section wound healing - consider referral if not resolving",
      trigger: `Wound status: ${csectionWound.status}, severity: ${csectionWound.severity}`,
      niceRef: "NG194 1.5",
    });
  }

  // ── 🟢 Return-to-exercise guidance ───────────────────────────────────────
  // Always included as general guidance
  if (data.snapshot.completedWeek >= 4) {
    actions.push({
      urgency: "green",
      urgencyLabel: "Guidance",
      action: "Return-to-exercise guidance - discuss safe physical activity",
      trigger: "Programme Week 4+ completed",
      niceRef: "NG194 1.8",
    });
  }

  // Cap at 6 actions, prioritised by urgency
  return actions.slice(0, 6);
}
